import {
  Component,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { PoService } from './po.service';
import { AuthService } from '../../auth/auth.service';
import { CommonModule } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  Subject,
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  tap,
  switchMap,
  catchError,
  of,
  startWith,
} from 'rxjs';
import { Product } from '../products/products.model';
import { RouterLink } from '@angular/router';
import { ExcelUploadComponent } from '../../../shared/excel-upload/excel-upload.component';
import { environment } from '../../../environments/environment';
import { SearchSelectComponent } from '../../shared/components/search-select/search-select.component';
import { VendorService } from '../vendors/vendor.service';
import { PurchaseListFilters } from './po.service';

@Component({
  selector: 'app-po',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, RouterLink, ExcelUploadComponent, SearchSelectComponent],
  templateUrl: './po.component.html',
  styleUrls: ['./po.component.scss'],
})
export class PoComponent implements OnInit {
  products: any[] = [];
  searchTerm = '';
  currentPage = 1;
  pageSize = 10;
  selectedProduct: any = null;
  totalPurchaseOrders = 0;
  purchaseOrders: any[] = [];
  private poService = inject(PoService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  public authService = inject(AuthService);

  @ViewChild('addProductModal') addProductModalRef!: ElementRef;
  productToDelete: Product | null = null;
  deleteErrorMessage: string | null = null;
  pageCount: any;
  purchaseOrdersLoading = false;
  productInput$ = new Subject<string>();
  hasMoreProducts: boolean = false;
  searchInput$ = new Subject<string>();
  @ViewChild('confirmEmailModal') confirmEmailModalRef!: ElementRef;
  @ViewChild('recallModal') recallModalRef!: ElementRef;
  selectedPoIdForEmail: string | null = null;
  selectedPoForRecall: any = null;
  newExpiryDate: string = '';

  // Excel upload
  uploadUrl: string = `${environment.apiUrl}/purchase-order/upload/purchase-order`;
  showUploadResultModal = false;
  uploadResult: { processed: number; createdPOs: number; createdItems: number; errors: Array<{ row: number; error: string }> } | null = null;

  // Tab Filtering
  activeTab$ = new BehaviorSubject<'active' | 'expiredWithGrn' | 'expiredWithoutGrn'>('active');
  get activeTab() { return this.activeTab$.value; }
  expiredPoCount = 0;
  activePoCount = 0;
  expiredWithoutGrnPoCount = 0;

  // List filters (vendor / order-date range / status). Applied server-side
  // together with the active tab and the PO-number search; tab counts follow
  // the same filters so the numbers match the rows shown.
  private vendorService = inject(VendorService);
  vendors: any[] = [];
  readonly statusOptions = ['DRAFT', 'POSTED', 'CLOSED', 'CANCELLED'];
  filters: PurchaseListFilters = { vendorId: null, fromDate: '', toDate: '', status: '' };
  filters$ = new BehaviorSubject<PurchaseListFilters>({ ...this.filters });

  onFilterChange() {
    const next = { ...this.filters };
    if (JSON.stringify(next) === JSON.stringify(this.filters$.value)) return;
    this.currentPage = 1;
    this.filters$.next(next);
    this.loadPoCounts();
  }

  clearFilters() {
    this.filters = { vendorId: null, fromDate: '', toDate: '', status: '' };
    this.onFilterChange();
  }

  hasActiveFilters(): boolean {
    const f = this.filters;
    return !!(f.vendorId || f.fromDate || f.toDate || f.status);
  }

  // Open confirmation popup
  openConfirmEmailModal(poId: string) {
    this.selectedPoIdForEmail = poId;
    this.modalService.open(this.confirmEmailModalRef, {
      centered: true,
      backdrop: 'static',
    });
  }

  // Called after confirmation
  confirmSendEmail(modal: any) {
    if (!this.selectedPoIdForEmail) return;

    this.poService.sendEmail(this.selectedPoIdForEmail).subscribe({
      next: () => {
        this.toastr.success('Email sent successfully', 'Success');
        modal.close();
      },
      error: (err) => {
        this.toastr.error('Failed to send email', 'Error');
        console.error(err);
        modal.close();
      },
    });
  }

  private extractFilename(response: HttpResponse<Blob>, poId: string): string {
    const disposition = response.headers.get('Content-Disposition') || '';
    let match = disposition.match(/filename\*=([^;]+)/i);
    if (match && match[1]) {
      return decodeURIComponent(match[1].split("''").pop() || '');
    }
    match = disposition.match(/filename="?([^";]+)"?/i);
    if (match && match[1]) {
      return match[1];
    }
    return `purchase-order-${poId}.pdf`;
  }

  ngOnInit() {
    this.loadPoCounts();
    this.vendorService.getAllVendorsList().subscribe({
      next: (list) => (this.vendors = list || []),
      error: () => (this.vendors = []),
    });

    combineLatest([
      this.searchInput$.pipe(startWith(this.searchTerm)),
      this.activeTab$,
      this.filters$,
    ])
      .pipe(
        debounceTime(400),
        distinctUntilChanged((prev, curr) => {
          // only skip if term, tab AND filters are all the same
          return (
            prev[0] === curr[0] &&
            prev[1] === curr[1] &&
            JSON.stringify(prev[2]) === JSON.stringify(curr[2])
          );
        }),
        tap(() => {
          this.purchaseOrdersLoading = true;
        }),
        switchMap(([term, tab, filters]) => {
          let isExpired: boolean | undefined = undefined;
          let expiredWithoutGrn: boolean | undefined = undefined;

          if (tab === 'active') {
            isExpired = false;
          } else if (tab === 'expiredWithGrn') {
            isExpired = true;
          } else if (tab === 'expiredWithoutGrn') {
            expiredWithoutGrn = true;
          }

          if (!term || term.trim().length < 3) {
            // 🔹 fallback to normal paginated API
            return this.poService.getPurchaseOrders(
              {
                page: this.currentPage,
                limit: this.pageSize,
              },
              term, // pass the term even if < 3 to keep list updated
              isExpired,
              expiredWithoutGrn,
              filters,
            );
          } else {
            // 🔹 search only by PO number using tab context
            return this.poService.searchPo(term, isExpired, expiredWithoutGrn, filters).pipe(
              catchError(() => of([])) // return empty if API fails
            );
          }
        })
      )
      .subscribe((response: any) => {
        // normalize results
        if (Array.isArray(response)) {
          this.purchaseOrders = response;
          this.totalPurchaseOrders = response.length;
          this.pageCount = 1;
        } else if (response?.data) {
          this.purchaseOrders = response.data;
          this.totalPurchaseOrders = response.total;
          this.pageCount = response.pageCount;
        } else {
          this.purchaseOrders = response ? [response] : [];
          this.totalPurchaseOrders = this.purchaseOrders.length;
          this.pageCount = 1;
        }

        this.purchaseOrdersLoading = false;
      });
  }

  pageSizeChange() {
    this.currentPage = 1;
    this.loadPurchaseOrders();
  }

  loadPurchaseOrders() {
    let isExpired: boolean | undefined = undefined;
    let expiredWithoutGrn: boolean | undefined = undefined;

    if (this.activeTab === 'active') {
      isExpired = false;
    } else if (this.activeTab === 'expiredWithGrn') {
      isExpired = true;
    } else if (this.activeTab === 'expiredWithoutGrn') {
      expiredWithoutGrn = true;
    }

    this.poService
      .getPurchaseOrders(
        { page: this.currentPage, limit: this.pageSize },
        this.searchTerm,
        isExpired,
        expiredWithoutGrn,
        this.filters$.value,
      )
      .subscribe((response: any) => {
        this.purchaseOrders = response.data;
        this.totalPurchaseOrders = response.total;
        this.pageCount = response.pageCount;

        // Update the count for the current tab
        if (this.activeTab === 'active') {
          this.activePoCount = response.total;
        } else if (this.activeTab === 'expiredWithGrn') {
          this.expiredPoCount = response.total;
        } else if (this.activeTab === 'expiredWithoutGrn') {
          this.expiredWithoutGrnPoCount = response.total;
        }
      });
  }

  loadPoCounts() {
    // Fetch counts for tabs by making parallel calls with limit 1
    // Expired with GRN
    const filters = this.filters$.value;
    this.poService.getPurchaseOrders({ page: 1, limit: 1 }, '', true, undefined, filters).subscribe((res: any) => {
      this.expiredPoCount = res.total;
    });
    // Active
    this.poService.getPurchaseOrders({ page: 1, limit: 1 }, '', false, undefined, filters).subscribe((res: any) => {
      this.activePoCount = res.total;
    });
    // Expired Without GRN
    this.poService.getPurchaseOrders({ page: 1, limit: 1 }, '', undefined, true, filters).subscribe((res: any) => {
      this.expiredWithoutGrnPoCount = res.total;
    });
  }

  switchTab(tab: 'active' | 'expiredWithGrn' | 'expiredWithoutGrn') {
    if (this.activeTab === tab) return;
    this.activeTab$.next(tab);
    this.currentPage = 1;
    // We don't clear searchTerm anymore to allow persistent search context
  }

  onSearch() {
    this.searchInput$.next(this.searchTerm);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.currentPage) return;
    this.currentPage = page;
    this.loadPurchaseOrders(); // load the data for this page
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  downloadPo(poId: string) {
    this.poService
      .downloadPurchaseOrder(poId)
      .subscribe((response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const filename = this.extractFilename(response, poId);

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  // Close the modal manually
  closeModal() {
    this.modalService.dismissAll();
  }

  sendEmail(poId: string) {
    this.poService.sendEmail(poId).subscribe({
      next: (response: any) => {
        this.toastr.success('Email sent successfully', 'Success');
      },
      error: (error) => {
        this.toastr.error('Failed to send email', 'Error');
        console.error('Email error:', error);
      },
    });
  }

  // Recall PO Methods
  openRecallModal(po: any) {
    this.selectedPoForRecall = po;
    this.newExpiryDate = po.expiryDate ? po.expiryDate.split('T')[0] : '';
    this.modalService.open(this.recallModalRef, {
      centered: true,
      backdrop: 'static',
    });
  }

  extendExpiryDate(modal: any) {
    if (!this.selectedPoForRecall || !this.newExpiryDate) return;

    const payload = {
      expiryDate: new Date(this.newExpiryDate).toISOString(),
    };

    this.poService.updatePo(this.selectedPoForRecall.id, payload).subscribe({
      next: () => {
        this.toastr.success('PO Expiry Date extended successfully', 'Success');
        this.loadPoCounts();
        this.loadPurchaseOrders();
        modal.close();
      },
      error: (err) => {
        this.toastr.error('Failed to extend PO expiry date', 'Error');
        console.error(err);
      },
    });
  }

  downloadTemplate() {
    this.poService.downloadTemplate().subscribe({
      next: (blob: any) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'purchase_order_template.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.toastr.success('Template downloaded successfully.');
      },
      error: (err) => {
        console.error('Template download error:', err);
        this.toastr.error('Failed to download template.');
      },
    });
  }

  onUploadSuccess(res: any) {
    const data = res?.body || res;
    this.uploadResult = {
      processed: data?.processed ?? 0,
      createdPOs: data?.createdPOs ?? 0,
      createdItems: data?.createdItems ?? 0,
      errors: data?.errors ?? [],
    };
    this.showUploadResultModal = true;
    this.loadPoCounts();
    // Use the reactive stream to reload instead of calling loadPurchaseOrders
    this.activeTab$.next(this.activeTab);
  }

  onUploadError(error: any) {
    console.error('PO Excel upload error:', error);
    const errorMessage = error?.error?.message || error?.message || 'Failed to process Excel file.';
    this.toastr.error(errorMessage, 'Upload Error');
  }

  closeUploadResultModal() {
    this.showUploadResultModal = false;
    this.uploadResult = null;
  }
}
